import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuthStore } from '@/stores/authStore';
import { PlanType, PLAN_LIMITS, getPlanLimits } from '@/lib/plan-limits';
import { toast } from 'sonner';

interface Subscription {
  id: string;
  user_id: string;
  plan: PlanType;
  status: 'active' | 'trialing' | 'past_due' | 'canceled' | 'incomplete';
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  trial_end: string | null;
  cancel_at_period_end: boolean;
}

interface UsageTracking {
  id: string;
  user_id: string;
  period_start: string;
  period_end: string;
  static_qr_count: number;
  dynamic_qr_count: number;
  scans_count: number;
  batch_count: number;
  ai_suggestions_count: number;
  api_requests_count: number;
}

interface Invoice {
  id: string;
  stripe_invoice_id: string | null;
  amount_paid: number;
  currency: string;
  status: string;
  invoice_pdf: string | null;
  hosted_invoice_url: string | null;
  period_start: string | null;
  period_end: string | null;
  created_at: string;
}

export function useSubscription() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  // Fetch subscription
  const { data: subscription, isLoading: isLoadingSubscription } = useQuery({
    queryKey: ['subscription', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) {
        // If no subscription exists, create free one
        if (error.code === 'PGRST116') {
          const { data: newSub, error: insertError } = await supabase
            .from('subscriptions')
            .insert({ user_id: user.id, plan: 'free', status: 'active' })
            .select()
            .single();
          
          if (insertError) throw insertError;
          return newSub as Subscription;
        }
        throw error;
      }
      return data as Subscription;
    },
    enabled: !!user?.id,
  });

  // Fetch current usage
  const { data: usage, isLoading: isLoadingUsage } = useQuery({
    queryKey: ['usage', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      // Get current period start
      const now = new Date();
      const periodStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      
      const { data, error } = await supabase
        .from('usage_tracking')
        .select('*')
        .eq('user_id', user.id)
        .eq('period_start', periodStart)
        .single();

      if (error) {
        // If no usage record, create one
        if (error.code === 'PGRST116') {
          const periodEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
          const { data: newUsage, error: insertError } = await supabase
            .from('usage_tracking')
            .insert({ 
              user_id: user.id, 
              period_start: periodStart,
              period_end: periodEnd 
            })
            .select()
            .single();
          
          if (insertError) throw insertError;
          return (newUsage as unknown) as UsageTracking;
        }
        throw error;
      }
      return (data as unknown) as UsageTracking;
    },
    enabled: !!user?.id,
  });

  // Fetch invoices
  const { data: invoices = [], isLoading: isLoadingInvoices } = useQuery({
    queryKey: ['invoices', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('invoices')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Invoice[];
    },
    enabled: !!user?.id,
  });

  // Get plan limits
  const plan = subscription?.plan || 'free';
  const limits = getPlanLimits(plan);

  // Check if at limit
  const isAtDynamicQRLimit = () => {
    if (limits.dynamicQRLimit === -1) return false;
    return (usage?.dynamic_qr_count || 0) >= limits.dynamicQRLimit;
  };

  const isAtStaticQRLimit = () => {
    if (limits.staticQRLimit === -1) return false;
    return (usage?.static_qr_count || 0) >= limits.staticQRLimit;
  };

  const isAtScansLimit = () => {
    if (limits.scansPerMonth === -1) return false;
    return (usage?.scans_count || 0) >= limits.scansPerMonth;
  };

  const isAtBatchLimit = () => {
    if (limits.batchLimit === 0) return true;
    if (limits.batchLimit === -1) return false;
    return (usage?.batch_count || 0) >= limits.batchLimit;
  };

  const isAtAISuggestionsLimit = () => {
    if (limits.aiSuggestionsPerMonth === -1) return false;
    if (limits.aiSuggestionsPerMonth === 0) return true;
    return (usage?.ai_suggestions_count || 0) >= limits.aiSuggestionsPerMonth;
  };

  const isAtAPIRequestsLimit = () => {
    if (limits.apiRequestsPerMonth === -1) return false;
    if (limits.apiRequestsPerMonth === 0) return true;
    return (usage?.api_requests_count || 0) >= limits.apiRequestsPerMonth;
  };

  // Usage percentages
  const getStaticQRUsagePercent = () => {
    if (limits.staticQRLimit === -1) return 0;
    return Math.min(100, ((usage?.static_qr_count || 0) / limits.staticQRLimit) * 100);
  };

  const getDynamicQRUsagePercent = () => {
    if (limits.dynamicQRLimit === -1) return 0;
    return Math.min(100, ((usage?.dynamic_qr_count || 0) / limits.dynamicQRLimit) * 100);
  };

  const getScansUsagePercent = () => {
    if (limits.scansPerMonth === -1) return 0;
    return Math.min(100, ((usage?.scans_count || 0) / limits.scansPerMonth) * 100);
  };

  // Increment usage mutation
  const incrementUsage = useMutation({
    mutationFn: async ({ field, amount = 1 }: { field: string; amount?: number }) => {
      if (!user?.id) throw new Error('Not authenticated');
      
      const { error } = await supabase.rpc('increment_usage', {
        p_user_id: user.id,
        p_field: field,
        p_amount: amount,
      });
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usage', user?.id] });
    },
  });

  // Check feature access
  const canAccessFeature = (feature: keyof typeof limits.features): boolean => {
    return limits.features[feature];
  };

  const canDownloadFormat = (format: 'png' | 'svg' | 'pdf'): boolean => {
    return limits.downloadFormats.includes(format);
  };

  // Subscription status helpers
  const isTrialing = subscription?.status === 'trialing';
  const isPastDue = subscription?.status === 'past_due';
  const isCanceled = subscription?.status === 'canceled';
  const willCancelAtPeriodEnd = subscription?.cancel_at_period_end || false;

  const getDaysRemaining = () => {
    if (!subscription?.current_period_end) return null;
    const end = new Date(subscription.current_period_end);
    const now = new Date();
    const diff = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  };

  const getTrialDaysRemaining = () => {
    if (!subscription?.trial_end) return null;
    const end = new Date(subscription.trial_end);
    const now = new Date();
    const diff = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  };

  return {
    subscription,
    usage,
    invoices,
    plan: plan as PlanType,
    limits,
    isLoading: isLoadingSubscription || isLoadingUsage,
    isLoadingInvoices,
    
    // Limit checks
    isAtStaticQRLimit,
    isAtDynamicQRLimit,
    isAtScansLimit,
    isAtBatchLimit,
    isAtAISuggestionsLimit,
    isAtAPIRequestsLimit,
    
    // Usage percentages
    getStaticQRUsagePercent,
    getDynamicQRUsagePercent,
    getScansUsagePercent,
    
    // Feature access
    canAccessFeature,
    canDownloadFormat,
    
    // Status
    isTrialing,
    isPastDue,
    isCanceled,
    willCancelAtPeriodEnd,
    getDaysRemaining,
    getTrialDaysRemaining,
    
    // Actions
    incrementUsage: incrementUsage.mutate,
  };
}
